/**
 * Which branch this booking page is for (design note salon-multi-branch.md 9.2, 9.4; FR-SA-245).
 *
 * The tenant is the host, the branch is the first segment of the path: `naturals.simpro.app/madurai-kk-nagar`.
 * It is read once, at startup, like the tenant, and becomes the router's basename. Every link in the app
 * is written from the root ("/book", "/services") and so stays inside the branch without knowing it.
 * Choosing another branch is a page load to its address, which also empties the basket: the next
 * branch's prices, stylists and times are not this one's.
 *
 * A first segment that is a page of this app is not a branch. The server holds the same list
 * (`BranchSlug.Reserved`), so no branch can be given a slug that would hide a page.
 */
export const RESERVED_SEGMENTS: ReadonlySet<string> = new Set([
  "account", "admin", "api", "appointments", "articles", "assets", "b", "book", "branches", "gallery",
  "help", "home", "login", "logout", "my", "no-such-page", "notifications", "privacy", "services",
  "sign-in", "static", "terms", "waitlist",
]);

const SLUG = /^[a-z0-9](?:[a-z0-9-]{1,58}[a-z0-9])$/;

const REMEMBERED_KEY = "simpro.salon.branch";

/** The query that asks for the picker even when a branch is remembered: "Change branch". */
export const CHOOSE_BRANCH_QUERY = "choose";

/** The branch named by a path, or undefined for a path that starts with a page or nothing. */
export function branchFromPath(pathname: string): string | undefined {
  const first = pathname.split("/")[1]?.toLowerCase() ?? "";
  return first && SLUG.test(first) && !RESERVED_SEGMENTS.has(first) ? first : undefined;
}

function remembered(): string | undefined {
  try {
    return globalThis.localStorage?.getItem(REMEMBERED_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/** Kept per origin, so a customer who comes back to the bare link lands on their branch. */
export function rememberBranch(slug: string): void {
  try {
    globalThis.localStorage?.setItem(REMEMBERED_KEY, slug);
  } catch {
    // A private window without storage simply asks again next time.
  }
}

/** Opens a branch's page: a page load, so the app starts again inside it with an empty basket. */
export function openBranch(slug: string, path = "/"): void {
  rememberBranch(slug);
  globalThis.location.assign(`/${slug}${path === "/" ? "/" : path}`);
}

/** Opens the branch picker, whatever this phone remembered. */
export function chooseBranch(): void {
  globalThis.location.assign(`/?${CHOOSE_BRANCH_QUERY}=1`);
}

/**
 * A bare link on a phone that has chosen a branch before goes back to it, unless the customer asked
 * to choose again. Done before the router exists, so the first screen is already the branch's.
 */
function resolveAtStartup(): string | undefined {
  const location = globalThis.location;
  if (!location) return undefined;

  const fromPath = branchFromPath(location.pathname);
  if (fromPath) {
    rememberBranch(fromPath);
    return fromPath;
  }

  const wantsPicker = new URLSearchParams(location.search).has(CHOOSE_BRANCH_QUERY);
  const known = remembered();
  if (known && !wantsPicker && SLUG.test(known)) {
    location.replace(`/${known}${location.pathname}${location.search}${location.hash}`);
    return known;
  }
  return undefined;
}

const FROM_QR_KEY = "simpro.salon.from_qr";

/**
 * Whether this visit to the site began at a branch's counter QR code (`?src=qr`, OQ-SA-040's assumption):
 * kept for the tab, so a booking made a few pages later still counts as one from the counter.
 */
function readFromQr(): boolean {
  try {
    const location = globalThis.location;
    if (location && new URLSearchParams(location.search).get("src") === "qr") {
      globalThis.sessionStorage?.setItem(FROM_QR_KEY, "1");
      return true;
    }
    return globalThis.sessionStorage?.getItem(FROM_QR_KEY) === "1";
  } catch {
    return false;
  }
}

/** True when the customer came in through a counter QR code; their booking says so (FR-SA-247). */
export const cameFromQr: boolean = readFromQr();

/** The branch this page is for, or undefined: the salon's only branch, or the picker. */
export const branchSlug: string | undefined = resolveAtStartup();

/** The router's basename: the branch's path, or the root. */
export const branchBasename = branchSlug ? `/${branchSlug}` : "/";

/** The part of a public API path that names the branch, or the salon-wide path for one branch. */
export function publicPath(part: "" | "/catalog" | "/combos" | "/staff" | "/availability" | "/home" | "/gallery"): string {
  if (branchSlug) {
    return `/api/v1/salon/public/branches/${encodeURIComponent(branchSlug)}${part}`;
  }
  return part === "" ? "/api/v1/salon/public/profile" : `/api/v1/salon/public${part}`;
}
